import React, { useEffect, useState } from 'react';
import { 
  Calendar, Upload, Download, Trash2, Edit, X, Check, 
  ChevronDown, ChevronUp, Search, Filter, RefreshCw 
} from 'lucide-react';
import axios from 'axios';

const DAYS_OF_WEEK = [
  'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'
];

const DAY_LABELS = {
  'MONDAY': 'Monday',
  'TUESDAY': 'Tuesday',
  'WEDNESDAY': 'Wednesday',
  'THURSDAY': 'Thursday',
  'FRIDAY': 'Friday',
  'SATURDAY': 'Saturday',
  'SUNDAY': 'Sunday'
};

const HolidayCalendarManagement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Section 1: Holiday Calendar Selector
  const [holidayCalendars, setHolidayCalendars] = useState([]);
  const [selectedCalendarId, setSelectedCalendarId] = useState(null);

  // Section 2: Weekly Off Configuration
  const [weekOffs, setWeekOffs] = useState(['SATURDAY', 'SUNDAY']);
  const [savingConfig, setSavingConfig] = useState(false);

  // Section 3 & 4: Holiday Upload & Import Summary
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [duplicateAction, setDuplicateAction] = useState('skip');
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [pendingDuplicates, setPendingDuplicates] = useState([]);

  // Section 5: Holiday Master
  const [holidays, setHolidays] = useState([]);
  const [holidaySearch, setHolidaySearch] = useState('');
  const [holidayYearFilter, setHolidayYearFilter] = useState('');
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [showHolidayModal, setShowHolidayModal] = useState(false);

  // Section 6: Working Day Preview
  const [previewMonth, setPreviewMonth] = useState(new Date().getMonth() + 1);
  const [previewYear, setPreviewYear] = useState(new Date().getFullYear());
  const [workingDaysPreview, setWorkingDaysPreview] = useState(null);

  const fetchHolidayCalendars = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/v1/calendar/holiday-calendars');
      setHolidayCalendars(res.data);
      if (res.data.length > 0 && !selectedCalendarId) {
        setSelectedCalendarId(res.data[0].id);
      }
    } catch (e) {
      console.error(e);
      setError('Failed to load holiday calendars');
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarConfiguration = async (calendarId) => {
    try {
      const res = await axios.get(`/v1/calendar/configuration/${calendarId}`);
      setWeekOffs(res.data.week_offs || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchHolidays = async (calendarId) => {
    setLoading(true);
    try {
      const params = {};
      if (holidayYearFilter) params.year = holidayYearFilter;
      if (holidaySearch) params.search = holidaySearch;

      const res = await axios.get(`/v1/calendar/holiday-calendars/${calendarId}/holidays`, { params });
      setHolidays(res.data);
    } catch (e) {
      console.error(e);
      setError('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkingDaysPreview = async (calendarId) => {
    try {
      const res = await axios.get('/v1/calendar/working-days-preview', {
        params: {
          holiday_calendar_id: calendarId,
          payroll_month: previewMonth,
          payroll_year: previewYear
        }
      });
      setWorkingDaysPreview(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchHolidayCalendars();
  }, []);

  useEffect(() => {
    if (selectedCalendarId) {
      fetchCalendarConfiguration(selectedCalendarId);
      fetchHolidays(selectedCalendarId);
      fetchWorkingDaysPreview(selectedCalendarId);
    }
  }, [selectedCalendarId, holidayYearFilter, holidaySearch, previewMonth, previewYear]);

  const handleCalendarChange = (calendarId) => {
    setSelectedCalendarId(calendarId);
    setImportSummary(null);
    setWorkingDaysPreview(null);
  };

  const handleWeekOffToggle = (day) => {
    const newWeekOffs = weekOffs.includes(day)
      ? weekOffs.filter(d => d !== day)
      : [...weekOffs, day];

    // Validate at least one working day remains
    if (newWeekOffs.length >= 7) {
      setError('At least one working day must remain');
      return;
    }

    setWeekOffs(newWeekOffs);
  };

  const handleSaveConfiguration = async () => {
    setSavingConfig(true);
    setError('');
    try {
      await axios.post('/v1/calendar/configuration', {
        week_offs: weekOffs
      }, {
        params: { holiday_calendar_id: selectedCalendarId }
      });
      setSuccessMsg('Weekly off configuration saved successfully');
      fetchWorkingDaysPreview(selectedCalendarId);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadFile(file);
    setUploading(true);
    setError('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('holiday_calendar_id', selectedCalendarId);
    formData.append('action_on_duplicate', duplicateAction);

    try {
      const res = await axios.post('/v1/calendar/holidays/upload', formData, {
        params: {
          holiday_calendar_id: selectedCalendarId,
          action_on_duplicate: duplicateAction
        },
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportSummary(res.data);
      setSuccessMsg('Holiday import completed');
      fetchHolidays(selectedCalendarId);
      fetchWorkingDaysPreview(selectedCalendarId);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to upload holidays');
    } finally {
      setUploading(false);
      setUploadFile(null);
    }
  };

  const handleDownloadTemplate = () => {
    const template = [
      ['Sr No', 'Holiday Name', 'Holiday Date', 'Day'],
      ['1', 'Independence Day', '15-08-2026', 'Friday'],
      ['2', 'Republic Day', '26-01-2026', 'Monday']
    ];

    const csvContent = template.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'holiday_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDeleteHoliday = async (holidayId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Are you sure you want to delete this holiday?')) return;

    setLoading(true);
    try {
      await axios.delete(`/v1/calendar/holidays/${holidayId}`);
      setSuccessMsg('Holiday deleted successfully');
      fetchHolidays(selectedCalendarId);
      fetchWorkingDaysPreview(selectedCalendarId);
    } catch (e) {
      setError('Failed to delete holiday');
    } finally {
      setLoading(false);
    }
  };

  const handleEditHoliday = (holiday) => {
    setEditingHoliday(holiday);
    setShowHolidayModal(true);
  };

  const handleUpdateHoliday = async () => {
    if (!editingHoliday.holiday_name || !editingHoliday.holiday_date) {
      setError('Holiday name and date are required');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`/v1/calendar/holidays/${editingHoliday.id}`, {
        holiday_name: editingHoliday.holiday_name,
        holiday_date: editingHoliday.holiday_date,
        remarks: editingHoliday.remarks,
        is_active: editingHoliday.is_active
      });
      setSuccessMsg('Holiday updated successfully');
      setShowHolidayModal(false);
      setEditingHoliday(null);
      fetchHolidays(selectedCalendarId);
      fetchWorkingDaysPreview(selectedCalendarId);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to update holiday');
    } finally {
      setLoading(false);
    }
  };

  const getDayTypeColor = (dayType) => {
    switch (dayType) {
      case 'WORKING_DAY': return 'bg-green-100 text-green-800 border-green-200';
      case 'HOLIDAY': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'WEEK_OFF': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'HOLIDAY_ON_WEEK_OFF': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const selectedCalendar = holidayCalendars.find(c => c.id === selectedCalendarId);

  return (
    <div className="space-y-6 p-6 bg-gray-50/50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Holiday Calendar Management</h1>
          <p className="text-sm text-gray-500">Configure weekly offs, manage holidays, and preview working days</p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700 flex justify-between items-center border border-red-200">
          <span>{error}</span>
          <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      {successMsg && (
        <div className="rounded-lg bg-green-50 p-4 text-sm text-green-700 flex justify-between items-center border border-green-200">
          <span>{successMsg}</span>
          <button onClick={() => setSuccessMsg('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Section 1: Holiday Calendar Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Holiday Calendar</h2>
        <div className="flex flex-wrap gap-4">
          {holidayCalendars.map((calendar) => (
            <button
              key={calendar.id}
              onClick={() => handleCalendarChange(calendar.id)}
              className={`px-6 py-4 rounded-xl border-2 transition-all ${
                selectedCalendarId === calendar.id
                  ? 'border-blue-500 bg-blue-50 text-blue-900 shadow-md'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <div className="font-bold text-lg">{calendar.name}</div>
              <div className="text-xs text-gray-500 mt-1">{calendar.code}</div>
            </button>
          ))}
        </div>
      </div>

      {selectedCalendarId && (
        <>
          {/* Section 2: Weekly Off Configuration */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Weekly Off Configuration</h2>
              <button
                onClick={handleSaveConfiguration}
                disabled={savingConfig}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition"
              >
                {savingConfig ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {DAYS_OF_WEEK.map((day) => (
                <label
                  key={day}
                  className={`flex items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    weekOffs.includes(day)
                      ? 'border-purple-500 bg-purple-50 text-purple-900'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={weekOffs.includes(day)}
                    onChange={() => handleWeekOffToggle(day)}
                    className="sr-only"
                  />
                  <span className="font-medium">{DAY_LABELS[day]}</span>
                  {weekOffs.includes(day) && <Check className="w-4 h-4 ml-2" />}
                </label>
              ))}
            </div>
          </div>

          {/* Section 3 & 4: Holiday Upload & Import Summary */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Holiday Upload</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Upload Section */}
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 transition-colors">
                  <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-sm text-gray-600 mb-2">
                    Drag and drop Excel (.xlsx) or CSV file, or click to browse
                  </p>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="hidden"
                    id="holiday-upload"
                  />
                  <label
                    htmlFor="holiday-upload"
                    className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 cursor-pointer disabled:opacity-50"
                  >
                    {uploading ? 'Uploading...' : 'Select File'}
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <button
                    onClick={handleDownloadTemplate}
                    className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download Template</span>
                  </button>

                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-600">On Duplicate:</label>
                    <select
                      value={duplicateAction}
                      onChange={(e) => setDuplicateAction(e.target.value)}
                      className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
                    >
                      <option value="skip">Skip</option>
                      <option value="replace">Replace</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Import Summary */}
              {importSummary && (
                <div className="bg-gray-50 rounded-xl p-6 space-y-3">
                  <h3 className="font-semibold text-gray-900">Import Summary</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-white p-3 rounded-lg">
                      <div className="text-gray-500">Total Rows</div>
                      <div className="font-bold text-xl text-gray-900">{importSummary.total_rows}</div>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                      <div className="text-green-600">Imported</div>
                      <div className="font-bold text-xl text-green-700">{importSummary.imported}</div>
                    </div>
                    <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                      <div className="text-amber-600">Duplicates</div>
                      <div className="font-bold text-xl text-amber-700">{importSummary.duplicates}</div>
                    </div>
                    <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                      <div className="text-red-600">Errors</div>
                      <div className="font-bold text-xl text-red-700">{importSummary.errors}</div>
                    </div>
                  </div>
                  {importSummary.skipped_details && importSummary.skipped_details.length > 0 && (
                    <div className="mt-3">
                      <details className="text-sm">
                        <summary className="cursor-pointer text-gray-600 hover:text-gray-900">
                          View Details ({importSummary.skipped_details.length})
                        </summary>
                        <ul className="mt-2 space-y-1 text-gray-500">
                          {importSummary.skipped_details.map((detail, idx) => (
                            <li key={idx} className="text-xs">{detail}</li>
                          ))}
                        </ul>
                      </details>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Section 5: Holiday Master */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Holiday Master</h2>
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search holidays..."
                    value={holidaySearch}
                    onChange={(e) => setHolidaySearch(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <select
                  value={holidayYearFilter}
                  onChange={(e) => setHolidayYearFilter(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">All Years</option>
                  {[2025, 2026, 2027, 2028].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                <button
                  onClick={() => fetchHolidays(selectedCalendarId)}
                  className="p-2 text-gray-500 hover:text-gray-700"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-gray-700 uppercase text-xs">
                  <tr>
                    <th className="px-4 py-3">Sr No</th>
                    <th className="px-4 py-3">Holiday Name</th>
                    <th className="px-4 py-3">Holiday Date</th>
                    <th className="px-4 py-3">Day Of Week</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {holidays.map((holiday, idx) => (
                    <tr key={holiday.id} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3 font-medium">{idx + 1}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{holiday.holiday_name}</td>
                      <td className="px-4 py-3">{holiday.holiday_date}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          holiday.is_optional ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {holiday.day_of_week}
                        </span>
                      </td>
                      <td className="px-4 py-3 space-x-2">
                        <button
                          onClick={() => handleEditHoliday(holiday)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteHoliday(holiday.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {holidays.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                        No holidays found for this calendar
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 6: Working Day Preview */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Working Day Preview</h2>
              <div className="flex items-center space-x-3">
                <select
                  value={previewMonth}
                  onChange={(e) => setPreviewMonth(parseInt(e.target.value))}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(0, i).toLocaleString('en', { month: 'long' })}
                    </option>
                  ))}
                </select>
                <select
                  value={previewYear}
                  onChange={(e) => setPreviewYear(parseInt(e.target.value))}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                >
                  {[2025, 2026, 2027, 2028].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>

            {workingDaysPreview ? (
              <div className="space-y-6">
                {/* Attendance Period Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                    <div className="text-xs text-blue-600 font-medium">Attendance Period</div>
                    <div className="font-bold text-blue-900 mt-1">
                      {workingDaysPreview.attendance_from} to {workingDaysPreview.attendance_to}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                    <div className="text-xs text-gray-600 font-medium">Total Days</div>
                    <div className="font-bold text-gray-900 mt-1 text-2xl">{workingDaysPreview.total_days}</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                    <div className="text-xs text-green-600 font-medium">Working Days</div>
                    <div className="font-bold text-green-900 mt-1 text-2xl">{workingDaysPreview.working_days}</div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                    <div className="text-xs text-purple-600 font-medium">Excluded Days</div>
                    <div className="font-bold text-purple-900 mt-1 text-2xl">
                      {workingDaysPreview.weekly_off_dates.length + workingDaysPreview.holiday_dates.length - workingDaysPreview.holiday_overlaps.length}
                    </div>
                  </div>
                </div>

                {/* Detailed Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                    <div className="text-xs text-amber-600 font-medium">Holiday Dates</div>
                    <div className="font-bold text-amber-900 mt-1">{workingDaysPreview.holiday_dates.length}</div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                    <div className="text-xs text-purple-600 font-medium">Weekly Off Dates</div>
                    <div className="font-bold text-purple-900 mt-1">{workingDaysPreview.weekly_off_dates.length}</div>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-xl border border-orange-200">
                    <div className="text-xs text-orange-600 font-medium">Holiday Overlaps</div>
                    <div className="font-bold text-orange-900 mt-1">{workingDaysPreview.holiday_overlaps.length}</div>
                  </div>
                </div>

                {/* Miniature Calendar Grid */}
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="grid grid-cols-7 bg-gray-100 text-xs font-bold text-gray-700 uppercase tracking-wider">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                      <div key={day} className="px-2 py-2 text-center border-r border-gray-200 last:border-r-0">{day}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7">
                    {workingDaysPreview.calendar_grid.length > 0 && 
                      Array.from({ length: new Date(workingDaysPreview.calendar_grid[0].date).getDay() }).map((_, i) => (
                        <div key={`empty-${i}`} className="p-2 border-r border-b border-gray-100 min-h-[60px] bg-gray-50"></div>
                      ))
                    }
                    {workingDaysPreview.calendar_grid.map((item, idx) => (
                      <div
                        key={idx}
                        className={`p-2 border-r border-b border-gray-100 min-h-[60px] ${getDayTypeColor(item.day_type)}`}
                        title={`${item.date} - ${item.day_type}`}
                      >
                        <div className="text-xs font-semibold">{new Date(item.date).getDate()}</div>
                        <div className="text-[9px] mt-0.5 opacity-75">{item.day_of_week.slice(0, 3)}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Legend */}
                <div className="flex flex-wrap gap-4 text-xs">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-green-100 border border-green-200 rounded"></div>
                    <span className="text-gray-600">Working Day</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-amber-100 border border-amber-200 rounded"></div>
                    <span className="text-gray-600">Holiday</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-purple-100 border border-purple-200 rounded"></div>
                    <span className="text-gray-600">Week Off</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-orange-100 border border-orange-200 rounded"></div>
                    <span className="text-gray-600">Holiday on Week Off</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                <Calendar className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <p className="text-sm text-gray-600">Select a payroll month to preview working days</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Edit Holiday Modal */}
      {showHolidayModal && editingHoliday && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <h3 className="font-bold text-gray-900">Edit Holiday</h3>
              <button onClick={() => setShowHolidayModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Holiday Name *</label>
                <input
                  type="text"
                  value={editingHoliday.holiday_name}
                  onChange={(e) => setEditingHoliday({ ...editingHoliday, holiday_name: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Holiday Date *</label>
                <input
                  type="date"
                  value={editingHoliday.holiday_date}
                  onChange={(e) => setEditingHoliday({ ...editingHoliday, holiday_date: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  rows={2}
                  value={editingHoliday.remarks || ''}
                  onChange={(e) => setEditingHoliday({ ...editingHoliday, remarks: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-3 border-t border-gray-100">
              <button
                onClick={() => setShowHolidayModal(false)}
                className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateHoliday}
                disabled={loading}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 transition disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HolidayCalendarManagement;
